import { useCallback, useMemo } from 'react';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import { useCompanyInstalledAppsStore } from 'stores/companyInstalledAppsStore';
import { useMemberPermissionsStore } from 'stores/memberPermissionsStore';
import { useUserProfileStore } from 'stores/userProfileStore';
import {
  hasAppPermissionAccess,
  isEmployeePortalScopedPath,
  isUserLevelPath,
  memberCanAccessPath,
  memberHasCompanySettingsPermission,
  memberHasEmployeePortalAttendanceAccess,
  memberHasEmployeePortalBaseAccess,
  memberHasHrisPermission,
  memberHasJournalPermission,
  memberHasJournalsAreaAccess,
  memberHasUploadEntryPermission,
  memberHasUploadEntryUploadOnlyPermission,
  memberHasSendCustomerEmailsPermission,
  memberHasTaxCalendarPermission,
  memberHasTaxCalendarViewOnlyPermission,
  memberHasBusinessCalendarPermission,
  memberHasReportsHubAccess,
  COMPANY_SETTINGS_PERMISSION_IDS,
  JOURNALS_MODAL_PERMISSION_IDS,
  type CompanySettingsPermissionId,
  type JournalsModalPermissionId,
} from 'utils/appPermissions';
import {
  getAccessibleDownloadablesVisibility,
  isDownloadablesPath,
} from 'utils/downloadablesVisibility';
import { permissionIds } from 'utils/permissionIds';

const MEMBER_PERMISSION_ACTIONS = {
  recordIncome: permissionIds.recordIncome,
  recordExpenses: permissionIds.recordExpenses,
  billerManagement: permissionIds.billerManagement,
  arInvoices: permissionIds.arInvoices,
  equipmentManagement: permissionIds.equipmentManagement,
  capitalManagement: permissionIds.capitalManagement,
  recordCustomTransactions: permissionIds.recordCustomTransactions,
  categories: permissionIds.categories,
  customers: permissionIds.customers,
  sendCustomerEmails: permissionIds.sendCustomerEmails,
  billInvoiceSettings: permissionIds.billInvoiceSettings,
  suppliers: permissionIds.suppliers,
  uploadEntry: permissionIds.uploadEntry,
  uploadEntryUploadOnly: permissionIds.uploadEntryUploadOnly,
  taxCalendar: permissionIds.taxCalendar,
  taxCalendarViewOnly: permissionIds.taxCalendarViewOnly,
  withholdingTaxReports: permissionIds.withholdingTaxReports,
  businessCalendar: permissionIds.businessCalendar,
  eventsManagement: permissionIds.eventsManagement,
  reserve: permissionIds.reserve,
  fileManager: permissionIds.fileManager,
  dashboardTaxReminders: permissionIds.dashboardTaxReminders,
  reportsIncomeStatement: permissionIds.reportsIncomeStatement,
  reportsTrialBalance: permissionIds.reportsTrialBalance,
  reportsBalanceSheet: permissionIds.reportsBalanceSheet,
  reportsCashFlow: permissionIds.reportsCashFlow,
  reportsEquity: permissionIds.reportsEquity,
  reportsFs: permissionIds.reportsFs,
  reportsCashReceiptsJournal: permissionIds.reportsCashReceiptsJournal,
  reportsCashDisbursementJournal: permissionIds.reportsCashDisbursementJournal,
  reportsLedger: permissionIds.reportsLedger,
  reportsGeneralJournal: permissionIds.reportsGeneralJournal,
  posSystem: permissionIds.posSystem,
  dashboardTotalIncome: permissionIds.dashboardTotalIncome,
  dashboardTotalExpenses: permissionIds.dashboardTotalExpenses,
  dashboardTotalProfit: permissionIds.dashboardTotalProfit,
  dashboardRevenueExpensesChart: permissionIds.dashboardRevenueExpensesChart,
  dashboardPnlSummary: permissionIds.dashboardPnlSummary,
  dashboardRecentTransactions: permissionIds.dashboardRecentTransactions,
  dashboardExpenseBreakdown: permissionIds.dashboardExpenseBreakdown,
  settingsPeriodLocks: permissionIds.settingsPeriodLocks,
  settingsCurrency: permissionIds.settingsCurrency,
  settingsCategoryAccountNumbers: permissionIds.settingsCategoryAccountNumbers,
  editCompany: permissionIds.editCompany,
  viewCompany: permissionIds.viewCompanies,
  apiKeys: permissionIds.apiKeys,
  requests: permissionIds.requests,
  activityLogs: permissionIds.activityLogs,
  appsStore: permissionIds.appsStore,
  healthCheck: permissionIds.healthCheck,
  hris: permissionIds.hris,
  hrisEmployees: permissionIds.hrisEmployees,
  hrisAttendance: permissionIds.hrisAttendance,
  hrisLeave: permissionIds.hrisLeave,
  hrisPayroll: permissionIds.hrisPayroll,
  hrisSettings: permissionIds.hrisSettings,
  hrisReports: permissionIds.hrisReports,
  /** @deprecated Prefer employeePortal — legacy alias of portal base access. */
  hrisSelfService: permissionIds.hrisSelfService,
  employeePortal: permissionIds.employeePortal,
  employeePortalAttendance: permissionIds.employeePortalAttendance,
} as const;

type MemberPermissionAction = keyof typeof MEMBER_PERMISSION_ACTIONS;
type UserPermissionFlags = Record<MemberPermissionAction, boolean>;

interface NavAccess {
  journals: boolean;
  reports: boolean;
}

function buildPermissionFlags(
  isOwner: boolean,
  permissions: string[],
): UserPermissionFlags {
  const check = (permissionId: string) => {
    // Restrictive flag: owners have full upload-entry access, not upload-only UI.
    if (permissionId === permissionIds.uploadEntryUploadOnly) {
      if (isOwner) {
        return false;
      }
      return memberHasUploadEntryUploadOnlyPermission(permissions);
    }
    // Restrictive flag: owners have full tax-calendar access, not view-only UI.
    if (permissionId === permissionIds.taxCalendarViewOnly) {
      if (isOwner) {
        return false;
      }
      return memberHasTaxCalendarViewOnlyPermission(permissions);
    }
    // Employee Portal is self-service only — no owner blanket grant.
    if (
      permissionId === permissionIds.employeePortal ||
      permissionId === permissionIds.hrisSelfService
    ) {
      return memberHasEmployeePortalBaseAccess(permissions);
    }
    if (permissionId === permissionIds.employeePortalAttendance) {
      return memberHasEmployeePortalAttendanceAccess(permissions);
    }
    if (isOwner) {
      return true;
    }
    if (
      JOURNALS_MODAL_PERMISSION_IDS.includes(
        permissionId as JournalsModalPermissionId,
      )
    ) {
      return memberHasJournalPermission(
        permissions,
        permissionId as JournalsModalPermissionId,
      );
    }
    if (permissionId === permissionIds.uploadEntry) {
      return memberHasUploadEntryPermission(permissions);
    }
    if (permissionId === permissionIds.sendCustomerEmails) {
      return memberHasSendCustomerEmailsPermission(permissions);
    }
    if (permissionId === permissionIds.taxCalendar) {
      return memberHasTaxCalendarPermission(permissions);
    }
    if (permissionId === permissionIds.businessCalendar) {
      return memberHasBusinessCalendarPermission(permissions);
    }
    if (
      permissionId === permissionIds.hris ||
      permissionId === permissionIds.hrisEmployees ||
      permissionId === permissionIds.hrisAttendance ||
      permissionId === permissionIds.hrisLeave ||
      permissionId === permissionIds.hrisPayroll ||
      permissionId === permissionIds.hrisSettings ||
      permissionId === permissionIds.hrisReports
    ) {
      return memberHasHrisPermission(permissions, permissionId);
    }
    if (
      COMPANY_SETTINGS_PERMISSION_IDS.includes(
        permissionId as CompanySettingsPermissionId,
      )
    ) {
      return memberHasCompanySettingsPermission(
        permissions,
        permissionId as CompanySettingsPermissionId,
      );
    }
    return hasAppPermissionAccess(permissions, permissionId);
  };

  return (
    Object.keys(MEMBER_PERMISSION_ACTIONS) as MemberPermissionAction[]
  ).reduce((flags, action) => {
    flags[action] = check(MEMBER_PERMISSION_ACTIONS[action]);
    return flags;
  }, {} as UserPermissionFlags);
}

function buildNavAccess(
  isReady: boolean,
  isOwner: boolean,
  permissions: string[],
): NavAccess {
  if (!isReady || isOwner) {
    return { journals: true, reports: true };
  }

  return {
    journals: memberHasJournalsAreaAccess(permissions),
    reports: memberHasReportsHubAccess(permissions),
  };
}

export function useUserPermissions(options?: { companyId?: string }) {
  const profile = useUserProfileStore((s) => s.profile);
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const companyId = options?.companyId ?? activeCompanyId;
  const isReady = Boolean(profile?.id && companyId);

  const isOwner = useMemo(() => {
    if (!companyId || !profile?.companies) {
      return false;
    }
    const company = profile.companies.find((c) => c.id === companyId);
    return company?.owned === true;
  }, [companyId, profile?.companies]);

  useMemberPermissionsStore((s) => s.version);
  const getPermissions = useMemberPermissionsStore((s) => s.getPermissions);
  const permissions = getPermissions(companyId, profile?.id);

  const permissionFlags = useMemo(
    () => buildPermissionFlags(isOwner, permissions),
    [isOwner, permissions],
  );
  const installedAppsCompanyId = useCompanyInstalledAppsStore(
    (s) => s.companyId,
  );
  const installedAppIds = useCompanyInstalledAppsStore(
    (s) => s.installedAppIds,
  );
  const installedAppsStatus = useCompanyInstalledAppsStore((s) => s.status);
  const downloadableAppsReady =
    installedAppsStatus === 'success' && installedAppsCompanyId === companyId;

  const navAccess = useMemo(
    () => buildNavAccess(isReady, isOwner, permissions),
    [isReady, isOwner, permissions],
  );

  const hasPermission = useCallback(
    (permissionId: string) => {
      if (permissionId === permissionIds.uploadEntryUploadOnly) {
        if (isOwner) {
          return false;
        }
        return memberHasUploadEntryUploadOnlyPermission(permissions);
      }
      if (permissionId === permissionIds.taxCalendarViewOnly) {
        if (isOwner) {
          return false;
        }
        return memberHasTaxCalendarViewOnlyPermission(permissions);
      }
      // Employee Portal is self-service only — no owner blanket grant.
      if (
        permissionId === permissionIds.employeePortal ||
        permissionId === permissionIds.hrisSelfService
      ) {
        return memberHasEmployeePortalBaseAccess(permissions);
      }
      if (permissionId === permissionIds.employeePortalAttendance) {
        return memberHasEmployeePortalAttendanceAccess(permissions);
      }
      if (isOwner) {
        return true;
      }
      if (
        JOURNALS_MODAL_PERMISSION_IDS.includes(
          permissionId as JournalsModalPermissionId,
        )
      ) {
        return memberHasJournalPermission(
          permissions,
          permissionId as JournalsModalPermissionId,
        );
      }
      if (permissionId === permissionIds.uploadEntry) {
        return memberHasUploadEntryPermission(permissions);
      }
      if (permissionId === permissionIds.sendCustomerEmails) {
        return memberHasSendCustomerEmailsPermission(permissions);
      }
      if (permissionId === permissionIds.taxCalendar) {
        return memberHasTaxCalendarPermission(permissions);
      }
      if (permissionId === permissionIds.businessCalendar) {
        return memberHasBusinessCalendarPermission(permissions);
      }
      if (
        COMPANY_SETTINGS_PERMISSION_IDS.includes(
          permissionId as CompanySettingsPermissionId,
        )
      ) {
        return memberHasCompanySettingsPermission(
          permissions,
          permissionId as CompanySettingsPermissionId,
        );
      }
      return hasAppPermissionAccess(permissions, permissionId);
    },
    [isOwner, permissions],
  );

  const canAccessAppPath = useCallback(
    (path: string) => {
      if (!isReady) {
        return isUserLevelPath(path);
      }
      if (isOwner) {
        if (isEmployeePortalScopedPath(path)) {
          return memberCanAccessPath(permissions, path);
        }
        return true;
      }
      return memberCanAccessPath(permissions, path);
    },
    [isReady, isOwner, permissions],
  );

  const canAccessPath = useCallback(
    (path: string) => {
      if (isDownloadablesPath(path)) {
        const installedAppIdSet = new Set(installedAppIds);
        return getAccessibleDownloadablesVisibility(
          (appId) => installedAppIdSet.has(appId),
          downloadableAppsReady,
          canAccessAppPath,
        ).hasVisibleDownloads;
      }
      return canAccessAppPath(path);
    },
    [canAccessAppPath, downloadableAppsReady, installedAppIds],
  );

  return {
    isReady,
    isOwner,
    navAccess,
    hasPermission,
    canAccessPath,
    ...permissionFlags,
  };
}
